import React, { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { userAPI, holidayGroupAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import BranchSelector from './BranchSelector';
import HolidayGroupSelector from './HolidayGroupSelector';
import styled from 'styled-components';
import { ModernButton, ModernInput, ModernSelect } from './SharedComponents';
import { FiX } from './Icons';

const Form = styled.form`
  width: 100%;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${(p) => p.theme.spacing.lg};

  h3 {
    margin: 0;
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${(p) => p.theme.colors.border.light};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  background: ${(p) => p.theme.colors.surface};
  color: ${(p) => p.theme.colors.text.secondary};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${(p) => p.theme.colors.surfaceAlt};
    color: ${(p) => p.theme.colors.text.primary};
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${(p) => p.theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(p) => p.theme.colors.text.secondary};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.92rem;
  color: ${(p) => p.theme.colors.text.primary};
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: ${(p) => p.theme.colors.primary};
    cursor: pointer;
  }
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  color: ${(p) => p.theme.colors.status.error};
  font-size: 0.8rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${(p) => p.theme.spacing.sm};
  margin-top: ${(p) => p.theme.spacing.xl};
  padding-top: ${(p) => p.theme.spacing.lg};
  border-top: 1px solid ${(p) => p.theme.colors.border.light};
`;

const EmployeeForm = ({ user, defaultBranch = null, onCancel, onSave }) => {
  const { user: currentUser, refreshUser } = useContext(AuthContext);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [holidayGroups, setHolidayGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    defaultValues: user || {
      username: '',
      name: '',
      email: '',
      role: 'makeup',
      holiday_group_id: '',
      is_moveable: false,
      is_active: true,
    },
  });

  useEffect(() => {
    fetchHolidayGroups();
    if (user) {
      if (user.home_branch_id) {
        setSelectedBranches([user.home_branch_id]);
      }
    } else if (defaultBranch) {
      setSelectedBranches([defaultBranch]);
    }
  }, [user, defaultBranch]);

  const fetchHolidayGroups = async () => {
    try {
      const response = await holidayGroupAPI.getAll();
      setHolidayGroups(response.data);
    } catch (error) {
      console.error('Error fetching holiday groups:', error);
    }
  };

  const onSubmit = async (data) => {
    setSubmitted(true);
    if (selectedBranches.length === 0 || !data.holiday_group_id || data.holiday_group_id === '') {
      return; // inline errors below the fields explain what's missing
    }

    setLoading(true);
    try {
      const transformedData = {
        ...data,
        home_branch_id: selectedBranches[0],
        is_moveable: Boolean(data.is_moveable),
        is_active: data.is_active === 'true' || data.is_active === true,
        holiday_group_id: parseInt(data.holiday_group_id),
      };

      if (user) {
        await userAPI.update(user.id, transformedData);
        if (currentUser && currentUser.id === user.id) {
          await refreshUser();
        }
        toast.success(`${transformedData.name} updated`);
      } else {
        await userAPI.create(transformedData);
        toast.success(`${transformedData.name} created`);
      }

      onSave();
    } catch (error) {
      console.error('Error saving employee:', error);
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranches(branchId ? [branchId] : []);
  };

  const canEditRole = currentUser && ['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor'].includes(currentUser.role);
  const canEditBranches = canEditRole;

  const holidayMissing = submitted && (!watch('holiday_group_id') || watch('holiday_group_id') === '');
  const branchMissing = submitted && selectedBranches.length === 0;

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <TitleRow>
        <h3>{user ? `Edit ${user.name}` : 'Add employee'}</h3>
        <CloseButton type="button" onClick={onCancel} aria-label="Close">
          <FiX size={16} />
        </CloseButton>
      </TitleRow>

      <FormGroup>
        <Label>Username *</Label>
        <ModernInput
          {...register('username', { required: 'Username is required' })}
          type="text"
          placeholder="Unique username for login"
        />
        {errors.username && <ErrorText>{errors.username.message}</ErrorText>}
      </FormGroup>

      <FormGroup>
        <Label>Name *</Label>
        <ModernInput
          {...register('name', { required: 'Name is required' })}
          type="text"
          placeholder="Full name"
        />
        {errors.name && <ErrorText>{errors.name.message}</ErrorText>}
      </FormGroup>

      <FormGroup>
        <Label>Email *</Label>
        <ModernInput
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^\S+@\S+$/i,
              message: 'Invalid email address',
            },
          })}
          type="email"
          placeholder="Must match their Lark account email"
        />
        {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
      </FormGroup>

      {canEditRole && (
        <FormGroup>
          <Label>Role *</Label>
          <ModernSelect {...register('role', { required: 'Role is required' })}>
            <option value="makeup">Makeup Artist</option>
            <option value="makeup_supervisor">Makeup Supervisor</option>
            <option value="photographer">Photographer</option>
            <option value="photographer_supervisor">Photographer Supervisor</option>
            <option value="retoucher">Retoucher</option>
            <option value="retoucher_supervisor">Retoucher Supervisor</option>
            <option value="store_manager">Store Manager</option>
            <option value="manager_assistant">Manager Assistant</option>
            <option value="admin">Admin</option>
          </ModernSelect>
          {errors.role && <ErrorText>{errors.role.message}</ErrorText>}
        </FormGroup>
      )}

      <FormGroup>
        <Label>Holiday group *</Label>
        <HolidayGroupSelector
          selectedGroupId={watch('holiday_group_id')}
          onGroupChange={(groupId) => setValue('holiday_group_id', groupId, { shouldValidate: true })}
        />
        {holidayMissing && <ErrorText>Holiday group is required</ErrorText>}
      </FormGroup>

      {canEditBranches && (
        <FormGroup>
          <Label>Home branch *</Label>
          <BranchSelector
            value={selectedBranches[0] || ''}
            onChange={handleBranchChange}
            placeholder="Select home branch for this employee"
          />
          {branchMissing && <ErrorText>Please select a home branch</ErrorText>}
        </FormGroup>
      )}

      <FormGroup>
        <CheckboxLabel>
          <input {...register('is_moveable')} type="checkbox" />
          Moveable employee (can work at different branches)
        </CheckboxLabel>
      </FormGroup>

      <FormGroup>
        <Label>Status</Label>
        <ModernSelect {...register('is_active')}>
          <option value={true}>Active</option>
          <option value={false}>Inactive</option>
        </ModernSelect>
      </FormGroup>

      <Footer>
        <ModernButton type="button" variant="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </ModernButton>
        <ModernButton type="submit" disabled={loading}>
          {loading ? 'Saving…' : (user ? 'Update' : 'Create')}
        </ModernButton>
      </Footer>
    </Form>
  );
};

export default EmployeeForm;
